import { useCallback, useLayoutEffect, useState, type RefObject } from 'react';

const intersectsContainerClip = (element: HTMLElement, container: HTMLElement): boolean => {
    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    return (
        elementRect.bottom > containerRect.top &&
        elementRect.top < containerRect.bottom &&
        elementRect.right > containerRect.left &&
        elementRect.left < containerRect.right
    );
};

/**
 * Indices of focusable children visible inside a container with overflow clipping (e.g. max-height + overflow hidden).
 */
export const useClippedFocusableIndices = (
    containerRef: RefObject<HTMLElement | null>,
    itemCount: number,
    itemSelector = 'button[data-interactive]'
): Set<number> | null => {
    const [focusableIndices, setFocusableIndices] = useState<Set<number> | null>(null);

    const update = useCallback(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        const items = container.querySelectorAll(itemSelector);
        const next = new Set<number>();

        items.forEach((node, index) => {
            if (intersectsContainerClip(node as HTMLElement, container)) {
                next.add(index);
            }
        });

        setFocusableIndices(next);
    }, [containerRef, itemSelector]);

    useLayoutEffect(() => {
        update();

        const container = containerRef.current;
        if (!container) {
            return;
        }

        const resizeObserver = new ResizeObserver(() => {
            update();
        });

        resizeObserver.observe(container);

        window.addEventListener('resize', update);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', update);
        };
    }, [update, itemCount]);

    return focusableIndices;
};
